import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const contactInfo = [
    { icon: MapPin, titleKey: "contact.visitUs", line1: "Odins gate 13B", line2: "0266 Oslo" },
    { icon: Phone, titleKey: "contact.callUs", line1: "+47 930 46 396", line2: "", href: "tel:+4793046396" },
    { icon: Mail, titleKey: "contact.emailUs", line1: "post@beautybykuleva.no", line2: "", href: "mailto:post@beautybykuleva.no" },
    { icon: Clock, titleKey: "contact.hours", line1: "Man–Lør: 09:30–18:00", line2: "Søndag: Etter avtale" },
  ];

  const faqData = [
    { qKey: "contact.faq.q1", aKey: "contact.faq.a1" },
    { qKey: "contact.faq.q2", aKey: "contact.faq.a2" },
    { qKey: "contact.faq.q3", aKey: "contact.faq.a3" },
    { qKey: "contact.faq.q4", aKey: "contact.faq.a4" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error(language === "no" ? "Vennligst fyll ut alle obligatoriske felt" : "Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    setError(false);
    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      toast.success(language === "no" ? "Takk! Meldingen er sendt." : "Thank you! Message sent.");
    } catch (err) {
      console.error("Error sending message:", err);
      setError(true);
      toast.error(language === "no" ? "Kunne ikke sende melding. Prøv igjen." : "Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#F9F5F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#4A4A4A] mb-6">
              {t("contact.title")} <span className="italic text-[#D4A5A5]">{t("contact.titleHighlight")}</span>
            </h1>
            <p className="text-[#8C8C8C] text-lg">{t("contact.subtitle")}</p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <motion.div
                  key={info.titleKey}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-soft p-6 text-center card-hover"
                  data-testid={`contact-info-${index}`}
                >
                  <div className="w-14 h-14 rounded-full bg-[#F9F5F6] flex items-center justify-center text-[#D4A5A5] mx-auto mb-4">
                    <IconComponent size={24} />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#4A4A4A] mb-2">
                    {t(info.titleKey)}
                  </h3>
                  {info.href ? (
                    <a href={info.href} className="text-[#8C8C8C] text-sm hover:text-[#D4A5A5]">{info.line1}</a>
                  ) : (
                    <p className="text-[#8C8C8C] text-sm">{info.line1}</p>
                  )}
                  {info.line2 && (
                    <p className="text-[#8C8C8C] text-sm">{info.line2}</p>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16 bg-[#F9F5F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl shadow-soft p-8"
            >
              {success ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-[#8E9E82]/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={40} className="text-[#8E9E82]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#4A4A4A] mb-4">
                    {t("contact.success.title")}
                  </h3>
                  <p className="text-[#8C8C8C] mb-6">{t("contact.success.description")}</p>
                  <Button
                    onClick={() => {
                      setSuccess(false);
                      setError(false);
                      setFormData({ name: "", email: "", phone: "", message: "", website: "" });
                    }}
                    data-testid="send-another-btn"
                    className="btn-primary rounded-full"
                  >
                    {t("contact.success.sendAnother")}
                  </Button>
                </div>
              ) : (
                <>
                  <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#4A4A4A] mb-6">
                    {t("contact.sendMessage")}
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-[#4A4A4A] font-medium">
                          {t("contact.form.name")} *
                        </Label>
                        <Input
                          id="name"
                          data-testid="contact-input-name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder={t("contact.form.placeholder.name")}
                          className="rounded-xl border-[#E8D0D0] h-12"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#4A4A4A] font-medium">
                          {t("contact.form.email")} *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="contact-input-email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder={t("contact.form.placeholder.email")}
                          className="rounded-xl border-[#E8D0D0] h-12"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#4A4A4A] font-medium">
                        {t("contact.form.phone")}
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="contact-input-phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder={t("contact.form.placeholder.phone")}
                        className="rounded-xl border-[#E8D0D0] h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-[#4A4A4A] font-medium">
                        {t("contact.form.message")} *
                      </Label>
                      <Textarea
                        id="message"
                        data-testid="contact-input-message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder={t("contact.form.placeholder.message")}
                        className="rounded-xl border-[#E8D0D0] min-h-[150px]"
                        required
                      />
                    </div>
                    {/* Honeypot field - hidden from users, catches bots */}
                    <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>
                    <Button
                      type="submit"
                      data-testid="contact-submit-btn"
                      disabled={submitting}
                      className="btn-primary rounded-full w-full sm:w-auto px-8 py-6"
                    >
                      {submitting ? t("contact.form.sending") : (
                        <>
                          {t("contact.form.submit")}
                          <Send size={18} className="ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </motion.div>

            {/* Map / Image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="rounded-2xl overflow-hidden shadow-soft h-[500px]"
            >
              <img
                src="https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800"
                alt="Beauty salon interior"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#4A4A4A] mb-6">
              {t("contact.faq.title")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-soft" data-testid={`faq-${index}`}>
                  <h3 className="font-semibold text-[#4A4A4A] mb-2">{t(faq.qKey)}</h3>
                  <p className="text-[#8C8C8C] text-sm">{t(faq.aKey)}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
